"use client";

import * as Popover from "@radix-ui/react-popover";
import { Activity, BarChart3, Table2, PieChart, TrendingUp, Filter, LayoutGrid, Map, Globe, MoreVertical, Trash2 } from "lucide-react";
import { Widget } from "@/modules/widgets/types";
import { MiniWidgetPreview } from "./MiniWidgetPreview";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
    ticker: Activity,
    chart: BarChart3,
    bar: BarChart3,
    line: TrendingUp,
    pie: PieChart,
    funnel: Filter,
    bubble: Globe,
    table: Table2,
    "top-offers": LayoutGrid,
    "table-map": Map,
    "audience-map": Map,
    "competitor-table": Table2,
};

const COLOR_MAP: Record<string, { bg: string; text: string }> = {
    ticker: { bg: "bg-emerald-50", text: "text-emerald-600" },
    chart: { bg: "bg-blue-50", text: "text-blue-600" },
    table: { bg: "bg-slate-50", text: "text-slate-600" },
    "table-map": { bg: "bg-teal-50", text: "text-teal-600" },
    "audience-map": { bg: "bg-teal-50", text: "text-teal-600" },
};

interface SharedWidgetCardProps {
    widget: Widget;
    isSelected: boolean;
    onClick: () => void;
    onRemove: () => void;
}

export function SharedWidgetCard({ widget, isSelected, onClick, onRemove }: SharedWidgetCardProps) {
    const typeKey = widget.chartType || widget.type;
    const Icon = ICON_MAP[typeKey] ?? BarChart3;
    const colors = COLOR_MAP[widget.type] ?? { bg: "bg-blue-50", text: "text-blue-600" };

    const previewConfig = {
        widgetType: widget.type,
        chartType: widget.chartType || undefined,
        title: widget.title,
        description: widget.description,
    };

    return (
        <div
            onClick={onClick}
            className={`w-full text-left rounded-xl border-2 overflow-hidden transition-all duration-150 cursor-pointer ${
                isSelected
                    ? "border-[#079a95] ring-2 ring-[#079a95]/20 shadow-md scale-[1.01]"
                    : "border-[#e5e7eb] hover:border-[#079a95]/40 hover:shadow-sm"
            }`}
        >
            {/* Preview */}
            <div className="h-44 overflow-hidden bg-[#f8fafa]">
                <MiniWidgetPreview widgetConfig={previewConfig} />
            </div>

            {/* Info */}
            <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                    <div className={`inline-flex rounded-md p-1.5 shrink-0 ${colors.bg}`}>
                        <Icon className={`h-3.5 w-3.5 ${colors.text}`} />
                    </div>
                    <p className="text-[14px] font-semibold text-[#2d3a3f] font-roboto leading-tight truncate flex-1">
                        {widget.title}
                    </p>

                    {/* Three-dots menu */}
                    <Popover.Root>
                        <Popover.Trigger asChild>
                            <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                className="shrink-0 p-1 rounded-full text-[#5a717a] hover:bg-[#f3f5f6] transition-colors"
                                aria-label="Widget options"
                            >
                                <MoreVertical className="h-4 w-4" />
                            </button>
                        </Popover.Trigger>
                        <Popover.Portal>
                            <Popover.Content
                                side="bottom"
                                align="end"
                                sideOffset={4}
                                onClick={(e) => e.stopPropagation()}
                                className="z-[10002] bg-white shadow-lg rounded-md py-1 border border-gray-200 min-w-[160px]"
                                onOpenAutoFocus={(e) => e.preventDefault()}
                            >
                                <Popover.Close asChild>
                                    <button
                                        type="button"
                                        onClick={onRemove}
                                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-roboto"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Remove from shared
                                    </button>
                                </Popover.Close>
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>
                </div>

                {widget.description && (
                    <p className="text-[12px] text-[#5a717a] font-roboto line-clamp-2">
                        {widget.description}
                    </p>
                )}
            </div>
        </div>
    );
}
